import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';
import { FiPlus, FiTrash2, FiArrowRight, FiImage, FiX } from 'react-icons/fi';
import { campaignsApi, uploadApi } from '../../utils/api';
import './CreateCampaignPage.css';
import {
  DELIVERABLE_TYPES,
  DELIVERABLE_TYPE_HE,
  canonicalDeliverableType,
} from '../../constants/deliverableTypes';

const CreateCampaignPage = () => {
  const navigate = useNavigate();
  const [requirements, setRequirements] = useState([{ type: 'reel', quantity: 1 }]);
  const [compensation, setCompensation] = useState([{ type: 'product', description: '' }]);
  const [selectedPlatforms, setSelectedPlatforms] = useState([]);
  const [campaignImage, setCampaignImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const fileInputRef = useRef(null);

  const platformOptions = [
    { value: 'instagram', label: 'אינסטגרם' },
    { value: 'tiktok', label: 'טיקטוק' },
    { value: 'facebook', label: 'פייסבוק' }
  ];

  const togglePlatform = (platform) => {
    setSelectedPlatforms(prev =>
      prev.includes(platform)
        ? prev.filter(p => p !== platform)
        : [...prev, platform]
    );
  };

  const {
    register,
    getValues
  } = useForm();

  const queryClient = useQueryClient();

  const createMutation = useMutation(
    (data) => campaignsApi.create(data),
    {
      onSuccess: () => {
        toast.success('הקמפיין נוצר בהצלחה!');
        queryClient.invalidateQueries('admin-campaigns');
        queryClient.invalidateQueries('my-campaigns');
        queryClient.invalidateQueries('campaigns');
        navigate('/dashboard/campaigns');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'שגיאה ביצירת הקמפיין');
      }
    }
  );

  const handleImageSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type (accept all image types including HEIC)
    const imageExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.heic', '.heif', '.avif', '.tiff', '.bmp'];
    const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    if (!file.type.startsWith('image/') && !imageExtensions.includes(ext)) {
      toast.error('יש לבחור קובץ תמונה');
      return;
    }

    // Validate file size (max 15MB)
    if (file.size > 15 * 1024 * 1024) {
      toast.error('גודל התמונה מקסימלי הוא 15MB');
      return;
    }

    // Show preview immediately
    const reader = new FileReader();
    reader.onload = (e) => setImagePreview(e.target.result);
    reader.readAsDataURL(file);

    // Upload image
    setUploadingImage(true);
    try {
      const response = await uploadApi.uploadImage(file);
      setCampaignImage(response.data.url);
      toast.success('התמונה הועלתה בהצלחה');
    } catch (error) {
      toast.error('שגיאה בהעלאת התמונה');
      setImagePreview(null);
    } finally {
      setUploadingImage(false);
    }
  };

  const removeImage = () => {
    setCampaignImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const saveDraft = () => {
    const data = getValues();
    if (!data.title?.trim()) {
      toast.error('כותרת היא שדה חובה');
      return;
    }

    const campaignData = {
      title: data.title,
      shortDescription: data.shortDescription,
      description: data.description,
      guidelines: data.guidelines,
      requirements,
      compensation,
      platforms: selectedPlatforms,
      coverImageUrl: campaignImage,
      minFollowers: data.minFollowers ? parseInt(data.minFollowers) : 0,
      status: 'draft'
    };
    createMutation.mutate(campaignData);
  };

  const publishCampaign = () => {
    const data = getValues();
    if (!data.title?.trim()) {
      toast.error('כותרת היא שדה חובה');
      return;
    }
    if (!data.description?.trim()) {
      toast.error('תיאור הוא שדה חובה');
      return;
    }
    if (!campaignImage) {
      toast.error('יש להעלות תמונת קמפיין');
      return;
    }

    const campaignData = {
      title: data.title,
      shortDescription: data.shortDescription,
      description: data.description,
      guidelines: data.guidelines,
      requirements,
      compensation,
      platforms: selectedPlatforms,
      coverImageUrl: campaignImage,
      minFollowers: data.minFollowers ? parseInt(data.minFollowers) : 0,
      status: 'active'
    };
    createMutation.mutate(campaignData);
  };

  const addRequirement = () => {
    setRequirements([...requirements, { type: 'reel', quantity: 1 }]);
  };

  const removeRequirement = (index) => {
    setRequirements(requirements.filter((_, i) => i !== index));
  };

  const updateRequirement = (index, field, value) => {
    const updated = [...requirements];
    updated[index][field] = value;
    setRequirements(updated);
  };

  const addCompensation = () => {
    setCompensation([...compensation, { type: 'product', description: '' }]);
  };

  const removeCompensation = (index) => {
    setCompensation(compensation.filter((_, i) => i !== index));
  };

  const updateCompensation = (index, field, value) => {
    const updated = [...compensation];
    updated[index][field] = value;
    setCompensation(updated);
  };

  return (
    <div className="create-campaign-page">
      <button className="back-btn" onClick={() => navigate('/dashboard/campaigns')}>
        <FiArrowRight />
        חזרה לקמפיינים
      </button>

      <div className="page-header">
        <h1>יצירת קמפיין חדש</h1>
        <p>מלא את הפרטים כדי ליצור קמפיין חדש</p>
      </div>

      <motion.form
        className="campaign-form"
        onSubmit={(e) => e.preventDefault()}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        {/* Basic Info */}
        <div className="form-section">
          <h2>פרטי הקמפיין</h2>

          <div className="form-group">
            <label className="form-label">כותרת הקמפיין *</label>
            <input
              type="text"
              className="form-input"
              placeholder="למשל: קמפיין קיץ 2024"
              {...register('title')}
            />
          </div>

          <div className="form-group">
            <label className="form-label">תיאור קצר</label>
            <input
              type="text"
              className="form-input"
              placeholder="תיאור קצר שיופיע בכרטיס הקמפיין"
              {...register('shortDescription')}
            />
          </div>

          <div className="form-group">
            <label className="form-label">תיאור מלא *</label>
            <textarea
              className="form-textarea"
              rows={5}
              placeholder="תאר את הקמפיין, המטרות והציפיות מהיוצרים..."
              {...register('description')}
            />
          </div>

          <div className="form-group">
            <label className="form-label">פלטפורמות</label>
            <div className="platforms-grid">
              {platformOptions.map(opt => (
                <label
                  key={opt.value}
                  className={`platform-option ${selectedPlatforms.includes(opt.value) ? 'selected' : ''}`}
                >
                  <input
                    type="checkbox"
                    checked={selectedPlatforms.includes(opt.value)}
                    onChange={() => togglePlatform(opt.value)}
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">תמונת קמפיין</label>
            <div className="image-upload-area">
              {imagePreview ? (
                <div className="image-preview">
                  <img src={imagePreview} alt="תצוגה מקדימה" />
                  <button
                    type="button"
                    className="remove-image-btn"
                    onClick={removeImage}
                  >
                    <FiX />
                  </button>
                  {uploadingImage && <div className="upload-overlay">מעלה...</div>}
                </div>
              ) : (
                <div
                  className="upload-placeholder"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <FiImage className="upload-icon" />
                  <span>לחץ להעלאת תמונה</span>
                  <span className="upload-hint">PNG, JPG, HEIC ועוד - עד 15MB</span>
                </div>
              )}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleImageSelect}
                accept="image/*,.heic,.heif,.avif"
                style={{ display: 'none' }}
              />
            </div>
          </div>
        </div>

        {/* Requirements */}
        <div className="form-section">
          <h2>דרישות</h2>

          {requirements.map((req, index) => (
            <div key={index} className="requirement-row">
              <select
                className="form-select"
                value={req.type}
                onChange={(e) => updateRequirement(index, 'type', e.target.value)}
              >
                {DELIVERABLE_TYPES.map((t) => (
                  <option key={t} value={t}>{DELIVERABLE_TYPE_HE[t]}</option>
                ))}
                {/* An unrecognised value keeps its own option so the select
                    shows what is actually stored. Without this the browser
                    falls back to the first option and the next save rewrites
                    the row as a reel. */}
                {req.type && !canonicalDeliverableType(req.type) && (
                  <option value={req.type}>{req.type}</option>
                )}
              </select>
              <input
                type="number"
                className="form-input quantity-input"
                min="1"
                value={req.quantity}
                onChange={(e) => updateRequirement(index, 'quantity', parseInt(e.target.value))}
              />
              <input
                type="text"
                className="form-input"
                placeholder="תיאור (אופציונלי)"
                value={req.description || ''}
                onChange={(e) => updateRequirement(index, 'description', e.target.value)}
              />
              {requirements.length > 1 && (
                <button
                  type="button"
                  className="btn btn-ghost btn-icon"
                  onClick={() => removeRequirement(index)}
                >
                  <FiTrash2 />
                </button>
              )}
            </div>
          ))}

          <button type="button" className="btn btn-outline btn-sm" onClick={addRequirement}>
            <FiPlus />
            הוסף דרישה
          </button>

          <div className="form-group mt-4">
            <label className="form-label">מינימום עוקבים</label>
            <input
              type="number"
              className="form-input"
              min="0"
              placeholder="0 = ללא מינימום"
              {...register('minFollowers')}
            />
          </div>
        </div>

        {/* Compensation */}
        <div className="form-section">
          <h2>תמורה</h2>

          {compensation.map((comp, index) => (
            <div key={index} className="compensation-row">
              <select
                className="form-select"
                value={comp.type}
                onChange={(e) => updateCompensation(index, 'type', e.target.value)}
              >
                <option value="paid">תשלום</option>
                <option value="product">מוצר</option>
                <option value="giftcard">גיפטקארד</option>
                <option value="barter">ברטר</option>
              </select>
              {comp.type === 'paid' && (
                <input
                  type="number"
                  className="form-input"
                  placeholder="סכום בש״ח"
                  value={comp.value || ''}
                  onChange={(e) => updateCompensation(index, 'value', parseInt(e.target.value))}
                />
              )}
              <input
                type="text"
                className="form-input"
                placeholder="תיאור התמורה"
                value={comp.description || ''}
                onChange={(e) => updateCompensation(index, 'description', e.target.value)}
              />
              {compensation.length > 1 && (
                <button
                  type="button"
                  className="btn btn-ghost btn-icon"
                  onClick={() => removeCompensation(index)}
                >
                  <FiTrash2 />
                </button>
              )}
            </div>
          ))}

          <button type="button" className="btn btn-outline btn-sm" onClick={addCompensation}>
            <FiPlus />
            הוסף תמורה
          </button>
        </div>

        {/* Guidelines */}
        <div className="form-section">
          <h2>דגשים חשובים</h2>

          <div className="form-group">
            <label className="form-label">דגשים חשובים ליוצרים</label>
            <textarea
              className="form-textarea"
              rows={4}
              placeholder="דגשים חשובים ליוצרים..."
              {...register('guidelines')}
            />
          </div>
        </div>

        {/* Submit */}
        <div className="form-actions">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={() => navigate('/dashboard/campaigns')}
          >
            ביטול
          </button>
          <button
            type="button"
            className="btn btn-outline"
            onClick={saveDraft}
            disabled={createMutation.isLoading}
          >
            {createMutation.isLoading ? 'שומר...' : 'שמור כטיוטה'}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={publishCampaign}
            disabled={createMutation.isLoading}
          >
            {createMutation.isLoading ? 'יוצר...' : 'פרסם קמפיין'}
          </button>
        </div>
      </motion.form>
    </div>
  );
};

export default CreateCampaignPage;
